/**
 * The files a workspace keeps beside its pages, and the one question every
 * surface has to ask about them: is this a document, or is it YAML?
 *
 * They are CONTENT — staged, committed, reviewed, published and exported like
 * any page, which is the whole point of keeping them in the customer's own
 * repository rather than in a settings table. What they are not is prose. A
 * page is Markdown and has a canonical form; `search.yaml` is a mapping, and
 * putting a Markdown canonicaliser through it does not tidy it, it destroys
 * it.
 *
 * Written down once because the comparison has been repeated at five sites in
 * three packages, and every copy of it named a different subset:
 *
 *  - the editor opened `nav.yaml` as text and the other five as documents;
 *  - the Blocks tab was disabled for `nav.yaml` and offered for the rest;
 *  - `GET /api/page` skipped the Markdown parser for `nav.yaml` and
 *    `variables.yaml` and ran it over the other four;
 *  - `PUT /api/page/doc` asked nothing at all;
 *  - and the workspace's own `isDataFile` — the one that got it right — was
 *    not exported, so nobody could have used it.
 *
 * Measured, on a workspace, through the real routes: open `search.yaml` in
 * the editor and save it without changing a character, and the file comes back
 * with its indentation gone and its brackets escaped. Every alias, synonym,
 * pin and weight in it stops working, silently, because a tuning file nobody
 * can parse is caught and ignored so that it cannot break search. The glossary
 * loses the vocabulary the linter enforces the same way. `style.yaml` is worse
 * than broken: `avoid:` ends up nested inside the first voice rule, so the file
 * still parses and means something else.
 *
 * `redirects.yaml` survived, which is luck rather than safety — its YAML is a
 * list of mappings, and a list of mappings happens to be what Markdown makes
 * of it. That is the most dangerous row in the table, because it is the one
 * that would have made a spot check say the problem does not exist.
 *
 * So: one predicate, in the package every surface already depends on — the
 * writer, the linter, the API, the command-line tool and the core.
 */
/** The navigation. Every project has one, and it is merged rather than copied. */
export const NAV_FILE = 'nav.yaml';
/**
 * The data files a workspace keeps beside its pages, other than the navigation.
 *
 * The order is the order they are named in, and callers that derive a list
 * from this — the lint input, the export, the importer — get a sixth on the
 * day it is added rather than on the day somebody remembers.
 */
export const DATA_FILES = [
    'variables.yaml',
    'search.yaml',
    'redirects.yaml',
    'glossary.yaml',
    'style.yaml',
];
/** The navigation and the data files: everything in a tree that is YAML. */
export const SITE_FILES = [NAV_FILE, ...DATA_FILES];
/**
 * Content that is YAML, not a document.
 *
 * The question the editor asks to choose a surface, the Blocks tab asks to
 * decide whether it may be pressed, and both write routes ask before they let
 * a Markdown serialiser near a customer's file.
 */
export function isSiteFile(path) {
    return SITE_FILES.includes(path);
}
//# sourceMappingURL=site-files.js.map